import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * Card — a container that groups related content and actions (a product, a
 * profile, a dashboard stat). Compose it from the slots: `CardHeader` (with
 * `CardTitle` / `CardDescription` / `CardAction`), `CardContent`, `CardFooter`.
 *
 *   <Card size="sm">
 *     <CardHeader>
 *       <CardTitle>Create project</CardTitle>
 *       <CardDescription>Deploy in one click.</CardDescription>
 *       <CardAction><Button size="xs">New</Button></CardAction>
 *     </CardHeader>
 *     <CardContent>…</CardContent>
 *     <CardFooter>…</CardFooter>
 *   </Card>
 *
 * `size` (`sm` | `md`, default `md`) scales the padding and the title /
 * description type down for compact layouts (lists, dashboards) — the corner
 * radius stays the same (`rounded-xl`) so cards read as one family at any size.
 * `size` is published to the slots via context, so you set it once on `Card`.
 */

type CardSize = 'sm' | 'md'

const CardContext = React.createContext<CardSize>('md')
const useCardSize = () => React.useContext(CardContext)

// Header gets full padding; content/footer drop their top padding so sections
// flow with one consistent gap (the header's bottom padding).
const headerPad: Record<CardSize, string> = { sm: 'p-4', md: 'p-6' }
const sectionPad: Record<CardSize, string> = { sm: 'p-4 pt-0', md: 'p-6 pt-0' }
const titleText: Record<CardSize, string> = { sm: 'text-base', md: 'text-lg' }
const descText: Record<CardSize, string> = { sm: 'text-xs', md: 'text-sm' }

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Scales padding + title/description type (radius is unchanged). */
  size?: CardSize
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ size = 'md', className, ...props }, ref) => (
    <CardContext.Provider value={size}>
      <div
        ref={ref}
        className={cn(
          'rounded-xl border border-fbui-gray-200 bg-fbui-base-white text-fbui-text-body shadow-fbui-sm',
          className
        )}
        {...props}
      />
    </CardContext.Provider>
  )
)
Card.displayName = 'Card'

/**
 * Header row. A grid that keeps `CardTitle` / `CardDescription` in the first
 * column and lifts `CardAction` into a right-aligned second column.
 */
export const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const size = useCardSize()
  return (
    <div
      ref={ref}
      className={cn(
        'grid auto-rows-min grid-cols-[1fr_auto] items-start gap-x-4 gap-y-1',
        headerPad[size],
        className
      )}
      {...props}
    />
  )
})
CardHeader.displayName = 'CardHeader'

/**
 * Card title. Renders a `<div>`, not a heading element, so it never disrupts the
 * page's heading outline — if a card needs a semantic heading for the document
 * structure, wrap the content in your own `<h2>`/`<h3>` at the right level.
 */
export const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const size = useCardSize()
  return (
    <div
      ref={ref}
      className={cn(
        'col-start-1 font-semibold leading-tight text-fbui-gray-900',
        titleText[size],
        className
      )}
      {...props}
    />
  )
})
CardTitle.displayName = 'CardTitle'

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const size = useCardSize()
  return (
    <p
      ref={ref}
      className={cn('col-start-1 text-fbui-gray-500', descText[size], className)}
      {...props}
    />
  )
})
CardDescription.displayName = 'CardDescription'

/** Right-aligned action slot (a button or menu) inside `CardHeader`. */
export const CardAction = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  // Pin to row 1 (not row-span-2): spanning both rows forces the header's second
  // grid track to exist even with no CardDescription, so the `gap-y-1` (4px) shows
  // as a phantom row. self-start keeps it top-aligned beside a 2-line title block.
  <div
    ref={ref}
    className={cn(
      'col-start-2 row-start-1 self-start justify-self-end',
      className
    )}
    {...props}
  />
))
CardAction.displayName = 'CardAction'

export const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const size = useCardSize()
  return (
    <div ref={ref} className={cn(sectionPad[size], className)} {...props} />
  )
})
CardContent.displayName = 'CardContent'

export const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const size = useCardSize()
  return (
    <div
      ref={ref}
      className={cn('flex items-center', sectionPad[size], className)}
      {...props}
    />
  )
})
CardFooter.displayName = 'CardFooter'
